import React from 'react'
import { Translations } from '../../../../shared/i18n/Translations'
import type {
    ContactItemType,
    CustomLink,
    ProfileInfo,
    UserProfile,
} from '../../../../shared/domain/users/types.no-deps'
import { getMsTeamsLinkHref, getPhoneNumberLinkHref, getWhatsAppLinkHref } from '../../../../shared/lib/url.no-deps'
import { ContactList } from './ContactList'
import { PersonalInfoRow } from './PersonalInfoRow'
import { SVG_Phone } from '../../svg/SVG_Phone'
import { SVG_WhatsApp } from '../../svg/SVG_WhatsApp'
import { SVG_MSTeams } from '../../svg/SVG_MSTeams'
import { SVG_EmailAt } from '../../svg/SVG_EmailAt'
import { SVG_URL } from '../../svg/SVG_URL'

export function PhoneNumberSection(
    profileInfo: ProfileInfo,
    onClickContactItemEllipsis?: (type: ContactItemType, index: number) => void,
    unEditable?: boolean,
    linkableProtocol?: string
) {
    return (
        profileInfo.contacts?.phoneNumbers?.length > 0 && (
            <PersonalInfoRow icon={<SVG_Phone />}>
                <ContactList
                    items={profileInfo.contacts.phoneNumbers.map((phoneNumber, i) => {
                        const title = linkableProtocol ? (
                            <a href={getPhoneNumberLinkHref(linkableProtocol, phoneNumber)} target="_blank">
                                {phoneNumber}
                            </a>
                        ) : (
                            <>{phoneNumber}</>
                        )

                        return {
                            onClick: unEditable ? undefined : () => onClickContactItemEllipsis('phone', i),
                            subtitle: Translations.t('app:Sett.phone'),
                            title,
                        }
                    })}
                />
            </PersonalInfoRow>
        )
    )
}

export function WhatsAppSection(
    profileInfo: ProfileInfo,
    onClickContactItemEllipsis?: (type: ContactItemType, index: number) => void,
    unEditable?: boolean,
    linkable?: boolean
) {
    return (
        profileInfo.contacts?.whatsApp?.length > 0 && (
            <PersonalInfoRow icon={<SVG_WhatsApp />} iconIsPrerendered={true}>
                <ContactList
                    items={profileInfo.contacts.whatsApp.map((whatsAppItem, i) => {
                        const title = linkable ? (
                            <a href={getWhatsAppLinkHref(whatsAppItem)} target="_blank">
                                {whatsAppItem}
                            </a>
                        ) : (
                            <>{whatsAppItem}</>
                        )

                        return {
                            onClick: unEditable ? undefined : () => onClickContactItemEllipsis('whatsApp', i),
                            subtitle: 'WhatsApp',
                            title,
                        }
                    })}
                />
            </PersonalInfoRow>
        )
    )
}

export function MsTeamsSection(
    profileInfo: ProfileInfo,
    onClickContactItemEllipsis?: (type: ContactItemType, index: number) => void,
    unEditable?: boolean,
    clickable?: boolean
) {
    return (
        profileInfo.contacts?.msTeams?.length > 0 && (
            <PersonalInfoRow icon={<SVG_MSTeams />} iconIsPrerendered={true}>
                <ContactList
                    items={profileInfo.contacts.msTeams.map((msTeam, i) => {
                        const title = clickable ? (
                            <a href={getMsTeamsLinkHref(msTeam)} target="_blank">
                                {msTeam}
                            </a>
                        ) : (
                            <>{msTeam}</>
                        )

                        return {
                            onClick: unEditable ? undefined : () => onClickContactItemEllipsis('msTeams', i),
                            subtitle: 'MS Teams',
                            title,
                        }
                    })}
                />
            </PersonalInfoRow>
        )
    )
}

function generateEmailLink(email: string): JSX.Element {
    return (
        <a href={`mailto:${email}`} target="_blank">
            {email}
        </a>
    )
}

function getEmailSectionData(
    user: UserProfile,
    onClickContactItemEllipsis?: (type: ContactItemType, index: number) => void,
    clickable?: boolean
) {
    const subtitle = Translations.t('app:Sett.contactInfo.email')
    const userEmails = (user.profileInfo.contacts?.emails || []).map((email, i) => {
        const title = clickable ? generateEmailLink(email) : <>{email}</>
        return {
            onClick: onClickContactItemEllipsis ? () => onClickContactItemEllipsis('email', i) : undefined,
            subtitle,
            title,
        }
    })

    const userPrimaryEmail = {
        subtitle,
        title: clickable ? generateEmailLink(user.email) : <>{user.email}</>,
    }

    return [userPrimaryEmail, ...userEmails]
}

export function EmailSection(
    user: UserProfile,
    onClickContactItemEllipsis: (type: ContactItemType, index: number) => void,
    renderContactsAsLinks: boolean
) {
    return (
        <PersonalInfoRow icon={<SVG_EmailAt />}>
            <ContactList items={getEmailSectionData(user, onClickContactItemEllipsis, renderContactsAsLinks)} />
        </PersonalInfoRow>
    )
}

function formatUrlLink(link: CustomLink): JSX.Element {
    let linkValue = link.value

    // Ensure the link has a protocol by adding 'https://' if it doesn't already contain one
    if (!linkValue.includes(':')) {
        linkValue = 'https://' + linkValue
    }

    return (
        <zaptic-button onClick={() => window.open(linkValue, '_blank')} size="small" type="link">
            {link.label}
        </zaptic-button>
    )
}

export function UrlSection(
    profileInfo: ProfileInfo,
    onClickContactItemEllipsis: (type: ContactItemType, index: number) => void,
    unEditable?: boolean
) {
    return (
        profileInfo.contacts?.links?.length > 0 && (
            <PersonalInfoRow icon={<SVG_URL />}>
                <ContactList
                    items={profileInfo.contacts.links.map((link, i) => ({
                        onClick: unEditable ? undefined : () => onClickContactItemEllipsis('url', i),
                        title: formatUrlLink(link),
                    }))}
                />
            </PersonalInfoRow>
        )
    )
}
